/**
 * WhatsAppStatsWidget - Phase 1: Dashboard Temps Réel
 * 
 * Widget affichant les statistiques temps réel des conversations WhatsApp.
 * Affiche: Total, Actives 24h, En takeover, En auto, Taux takeover.
 * 
 * Usage:
 * ```tsx
 * <WhatsAppStatsWidget />
 * ```
 */
import { useWhatsAppStats } from '../hooks/useWhatsAppStats'

export default function WhatsAppStatsWidget() {
  const { stats, loading, error } = useWhatsAppStats()
  
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-3"></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    )
  }
  
  if (error || !stats) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 mb-4 text-sm text-red-600 dark:text-red-400">
        ⚠️ Impossible de charger les statistiques
      </div>
    )
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
        📊 Statistiques WhatsApp
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          Temps réel
        </span>
      </h3>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {/* Total Conversations */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/30 rounded-lg p-3">
          <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
            {stats.total_conversations}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">
            💬 Total
          </div>
        </div>
        
        {/* Actives 24h */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-900/30 rounded-lg p-3">
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
            {stats.active_24h}
          </div>
          <div className="text-xs text-emerald-600 dark:text-emerald-500 mt-0.5">
            🔥 Actives 24h
          </div>
        </div>
        
        {/* En Takeover */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/30 rounded-lg p-3">
          <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
            {stats.in_takeover}
          </div>
          <div className="text-xs text-orange-600 dark:text-orange-500 mt-0.5">
            👤 En takeover
          </div>
        </div>
        
        {/* En Auto */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/30 rounded-lg p-3">
          <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
            {stats.in_auto}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-500 mt-0.5">
            🤖 En auto
          </div>
        </div>
        
        {/* Taux Takeover */}
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-900/30 rounded-lg p-3 col-span-2 sm:col-span-1">
          <div className="text-2xl font-bold text-pink-700 dark:text-pink-400">
            {stats.takeover_rate}%
          </div>
          <div className="text-xs text-pink-600 dark:text-pink-500 mt-0.5">
            📈 Taux takeover
          </div>
        </div>
      </div>
      
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
        Mis à jour automatiquement toutes les 30s
      </div>
    </div>
  )
}
